/**
 * Utility functions for Technique dropdown filtering.
 * Dynamically maps techniques from Master Data / Dropdown Config to report types.
 */
import { TECHNIQUE as STATIC_TECHNIQUE } from '../constants/dropdowns';

// Method keywords for report types
const REPORT_TYPE_KEYWORDS = {
  // Visual Testing
  "VT Cast": { prefixes: ["VT", "Visual"], values: ["Direct Visual Inspection", "Indirect Visual Inspection", "Remote Inspection"] },
  "VT Weld": { prefixes: ["VT", "Visual"], values: ["Direct Visual Inspection", "Indirect Visual Inspection", "Remote Inspection"] },
  
  // Penetrant Testing
  "PT Fluorescent": { prefixes: ["PT Fluorescent", "Fluorescent"], values: ["Fluorescent Solvent Removable"] },
  "PT Visible": { prefixes: ["PT Visible", "Visible"], values: ["Visible Solvent Removable"] },
  
  // Magnetic Particle Testing
  "MT Bench": { prefixes: ["MT Bench", "Bench"], values: ["Direct Head Shot & Coil Technique With Fluorescent Medium"] },
  "MT Yoke": { prefixes: ["MT Yoke", "Yoke"], values: ["Electromagnetic Yoke-DC Type"] },
  
  // Ultrasonic Testing
  "UT Weld": { prefixes: ["UT", "Ultrasonic", "Pulse"], values: ["Pulse UT Scan"] },
  "UT Cast": { prefixes: ["UT", "Ultrasonic", "Pulse"], values: ["Pulse UT Scan"] },
  "UT Plate Forge": { prefixes: ["UT", "Ultrasonic", "Pulse"], values: ["Pulse UT Scan"] },
  "UTG": { prefixes: ["UTG", "UT", "Thickness"], values: [] },
  
  // Radiographic Testing
  "RT Cast": { prefixes: ["RT", "Radiographic", "Gamma", "X-Ray"], values: [] },
  "RT Weld": { prefixes: ["RT", "Radiographic", "Gamma", "X-Ray"], values: [] },
};

/**
 * Returns technique options filtered for a specific report type.
 * Evaluates items dynamically so newly added items from Dropdown Config
 * (e.g. "Pulse UT Scan" with description "Used in UT Cast and UT Weld reports")
 * are automatically included.
 *
 * @param {string} reportType - e.g. "VT Weld", "UT Cast", "PT Fluorescent", etc.
 * @param {Array} allTechniques - List of technique objects from dropdown context or static list.
 * @returns {Array} Filtered list of technique objects.
 */
export const getTechniqueOptions = (reportType, allTechniques = null) => {
  const techniqueList = (allTechniques && Array.isArray(allTechniques) && allTechniques.length > 0)
    ? allTechniques
    : (STATIC_TECHNIQUE || []);

  if (!techniqueList || techniqueList.length === 0) {
    return [{ id: 1, value: "-", status: "Active" }];
  }

  const normalizedType = (reportType || "").trim();
  const config = REPORT_TYPE_KEYWORDS[normalizedType];

  // Active filter: allow items where status is not explicitly 'Inactive' (handles 'Active', '', undefined, null)
  const activeTechniques = techniqueList.filter(item => {
    if (!item) return false;
    const status = String(item.status || "").trim().toLowerCase();
    return status !== "inactive";
  });

  // Always find '-' default option
  const defaultOption = activeTechniques.find(t => String(t.value).trim() === "-") || { id: 1, value: "-", status: "Active" };

  if (!normalizedType || !config) {
    return activeTechniques;
  }

  const matches = activeTechniques.filter(item => {
    const val = String(item.value || "").trim();
    if (val === "-") return false; // Handled separately

    const desc = String(item.description || "").trim().toLowerCase();
    const typeLower = normalizedType.toLowerCase();

    // 1. Direct match in description: e.g. "Used in UT Cast and UT Weld reports"
    if (desc && desc.includes(typeLower)) {
      return true;
    }

    // 2. Check if description or value mentions any of the method prefixes for this report type
    // e.g. "UT" or "Pulse" or "Visual" or "Fluorescent" or "MT Bench"
    for (const prefix of config.prefixes) {
      const pLower = prefix.toLowerCase();
      // Match whole word or keyword in description or value
      const wordRegex = new RegExp(`(^|[\\s,/-])${pLower}([\\s,/-]|$)`, "i");
      if (wordRegex.test(desc) || wordRegex.test(val.toLowerCase())) {
        // Special case: Ensure Visible doesn't match Fluorescent items, and vice versa
        if (normalizedType === "PT Visible" && (desc.includes("fluorescent") || val.toLowerCase().includes("fluorescent"))) {
          continue;
        }
        if (normalizedType === "PT Fluorescent" && (val.toLowerCase().includes("visible") && !val.toLowerCase().includes("fluorescent"))) {
          continue;
        }
        // Special case: Ensure MT Yoke doesn't match Bench, and vice versa
        if (normalizedType === "MT Yoke" && (desc.includes("bench") || val.toLowerCase().includes("bench"))) {
          continue;
        }
        if (normalizedType === "MT Bench" && (desc.includes("yoke") || val.toLowerCase().includes("yoke"))) {
          continue;
        }
        return true;
      }
    }

    // 3. Check known baseline values for this report type
    if (config.values.some(knownVal => knownVal.toLowerCase() === val.toLowerCase())) {
      return true;
    }

    return false;
  });

  // Default option '-' is always included first, then filtered techniques
  return [defaultOption, ...matches.filter(m => m.id !== defaultOption.id && m.value !== "-")];
};
